import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, Typography, Spin, Alert, Rate, Input, Button, Tag, Space,
  message, Result, Divider
} from 'antd';
import {
  FileSearchOutlined, SendOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { rfpService } from '../services/api';
import '../styles/Phase5.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const RFPVendorResponse = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [project, setProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [candidate, setCandidate] = useState(null);

  // Scores keyed by requirement id: { requirementId: { score, notes } }
  const [scores, setScores] = useState({});

  useEffect(() => {
    validateAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateAndLoad = async () => {
    setLoading(true);
    try {
      const validationResult = await rfpService.validateToken(token);
      setTokenValid(true);
      // validate returns flat fields: project_id, project_title, candidate_id, etc.
      const projectInfo = {
        id: validationResult.project_id,
        name: validationResult.project_title,
        deadline: validationResult.expires_at,
      };
      setProject(projectInfo);

      const authToken = localStorage.getItem('token');
      if (!authToken) {
        setNeedsLogin(true);
        setLoading(false);
        return;
      }

      await acceptAndLoadData(projectInfo);
    } catch (error) {
      console.error('Token validation failed:', error);
      setTokenValid(false);
      setTokenError(
        error.response?.data?.detail || 'This invitation link is invalid or has expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  const acceptAndLoadData = async (proj) => {
    try {
      const acceptResult = await rfpService.acceptInvite(token);
      setAccepted(true);

      const projectId = proj?.id || acceptResult?.project_id;
      if (!projectId) {
        message.error('Unable to determine project. Please contact the RFP administrator.');
        return;
      }

      const [reqs, candidates] = await Promise.all([
        rfpService.getRequirements(projectId),
        rfpService.getCandidates(projectId),
      ]);

      setRequirements(reqs);
      if (candidates && candidates.length > 0) {
        setCandidate(candidates[0]);
      }
    } catch (error) {
      console.error('Failed to accept invite or load data:', error);
      message.error('Failed to load questionnaire data. Please try again.');
    }
  };

  const handleScoreChange = (requirementId, value) => {
    setScores((prev) => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        score: value,
      },
    }));
  };

  const handleNotesChange = (requirementId, value) => {
    setScores((prev) => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        notes: value,
      },
    }));
  };

  const handleSubmitAll = async () => {
    const scoredEntries = Object.entries(scores).filter(
      ([, val]) => val.score && val.score > 0
    );

    if (scoredEntries.length === 0) {
      message.warning('Please provide at least one score before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const projectId = project?.id;
      const scorePayload = scoredEntries.map(([requirementId, val]) => ({
        requirement_id: parseInt(requirementId, 10),
        candidate_id: candidate?.id,
        score: val.score,
        notes: val.notes || '',
      }));

      await rfpService.bulkSubmitScores(projectId, scorePayload);
      message.success('All scores submitted successfully.');
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit scores:', error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to submit scores. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'blue';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p5-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Validating your invitation..." />
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="p5-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Result
          status="error"
          title="Invalid Invitation"
          subTitle={tokenError}
        />
      </div>
    );
  }

  // Needs login/register
  if (needsLogin) {
    return (
      <div className="p5-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Result
          status="warning"
          title="Authentication Required"
          subTitle="You need to log in or create an account to respond to this RFP questionnaire."
          extra={[
            <Button type="primary" key="login" href="/login">
              Log In
            </Button>,
            <Button key="register" href="/register">
              Create Account
            </Button>,
          ]}
        />
      </div>
    );
  }

  // Submission complete
  if (submitted) {
    return (
      <div className="p5-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Result
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          status="success"
          title="Responses Submitted"
          subTitle="Thank you for completing the RFP questionnaire. Your scores and notes have been recorded."
        />
      </div>
    );
  }

  return (
    <div className="p5-page" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      {/* Project Header */}
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <FileSearchOutlined style={{ marginRight: 8 }} />
          {project?.name || 'RFP Questionnaire'}
        </Title>
        {project?.deadline && (
          <Text type="secondary">
            Deadline: {new Date(project.deadline).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Text>
        )}
      </Card>

      {/* Instructions */}
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Alert
          type="info"
          showIcon
          message="Instructions"
          description="Please review each requirement below and provide your assessment of your company's capability. Rate each requirement on a scale of 1 to 5 stars and optionally include notes or evidence to support your rating."
          style={{ border: 'none', background: 'transparent', padding: 0 }}
        />
      </Card>

      <Divider />

      {/* Requirements List */}
      {requirements.length === 0 ? (
        <Card bordered={false}>
          <Text type="secondary">No requirements have been added to this project yet.</Text>
        </Card>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {requirements.map((req) => (
            <Card key={req.id} bordered={false} className="p5-card">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ fontSize: 16 }}>{req.title || req.name}</Text>
                  {req.description && (
                    <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                      {req.description}
                    </Paragraph>
                  )}
                </div>

                <Space size="small" wrap>
                  {req.category && (
                    <Tag color="blue">{req.category}</Tag>
                  )}
                  {req.priority && (
                    <Tag color={getPriorityColor(req.priority)}>{req.priority}</Tag>
                  )}
                </Space>

                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Capability Rating
                  </Text>
                  <Rate
                    count={5}
                    value={scores[req.id]?.score || 0}
                    onChange={(value) => handleScoreChange(req.id, value)}
                  />
                </div>

                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Notes / Evidence
                  </Text>
                  <TextArea
                    rows={3}
                    placeholder="Provide supporting details, evidence, or notes about your capability..."
                    value={scores[req.id]?.notes || ''}
                    onChange={(e) => handleNotesChange(req.id, e.target.value)}
                  />
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      )}

      {/* Submit Button */}
      {requirements.length > 0 && (
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={<SendOutlined />}
            loading={submitting}
            onClick={handleSubmitAll}
          >
            Submit All Scores
          </Button>
        </div>
      )}
    </div>
  );
};

export default RFPVendorResponse;
